<template>
  <sticky-header-layout>
    <!-- Header -->
    <template #header>
      <div class="action-container justify-content-end">
        <span>
          <el-button
            :disabled="isSaving"
            class="cancel-button"
            size="mini"
            type="warning"
            icon="el-icon-close"
            @click="handleCancel"
          >{{ $t('button.cancel') }}</el-button>
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            class="save-button"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >{{ $t('button.save') }}</el-button>
        </span>
      </div>
    </template>
    <!-- Header -->

    <div v-loading="isLoading">
      <!-- Form -->
      <el-form
        ref="form"
        :disabled="isSaving"
        :model="form"
        class="round-border-form pt-3 mt-3"
        label-width="150px"
      >
        <template
          v-for="{ key, ...column } in formOrder"
        >
          <el-form-item
            v-if="key === 'id' && isEditMode"
            :label="$t(`table.${key}`)"
            :key="key"
          >
            <div>{{ form.id }}</div>
          </el-form-item>

          <el-form-item
            v-else-if="key === 'date'"
            :label="$t(`table.${key}`)"
            :key="key"
            :prop="key"
          >
            <el-date-picker
              v-model="form[key]"
              type="date"
              value-format="yyyy-MM-dd"
              :disabled="isEditMode"
            >
            </el-date-picker>
          </el-form-item>

          <!-- Input -->
          <el-form-item
            v-else-if="column.isInput"
            :label="$t(`table.${key}`)"
            :key="key"
            :rules="column.rules"
            :prop="key"
          >
            <el-input
              v-model.trim="form[key]"
              v-bind="column.custom"
              clearable
            />
          </el-form-item>
          <!-- Input -->
        </template>

        <el-form-item 
          prop="bannerIds"
          label="Date & Banner IDs"
        >
          <el-input 
            type="textarea" 
            v-model="bannerIds"
            autosize
          />
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            @click="handleAutoFill"
          >
            Auto Fill
          </el-button>
        </el-form-item>
      </el-form>
      <!-- Form -->
    </div>
  </sticky-header-layout>
</template>
  
<script>
//   import { checkImageURL } from '../../utils/validate'
  import { FormRules } from '../../utils/form'
  
  import {getBannerScheduleDetail, addBannerSchedule, editBannerSchedule} from '../../api/trendings'
  
  export default {
    name: 'BannerScheduleDetail',
    props: {
      id: {
        type: [String, Number],
        default: undefined,
      },
    },
    data() {
      const formRules = {
        requiredStringField: [FormRules.required],
      }
      const formOrder = [
        { key: 'id' },
        { key: 'date', isInput: false},
        { key: 'home_carousel_banner_ids', isInput: true},
        { key: 'home_right_banner_ids', isInput: true},
        { key: 'app_promotion_banner_id', isInput: true},
      ]
      return {
        isEdited: false,
        isLoading: false,
        isSaving: false,
        formOrder,
        bannerIds: '',
        form: {
          date: '',
          home_carousel_banner_ids: '',
          home_right_banner_ids: '',
          app_promotion_banner_id: '',
        },
      }
    },
    computed: {
      isEditMode() {
        return !!this.id
      },
    },
    watch: {
      form: {
        handler() {
          this.isEdited = true
        },
        deep: true,
      },
    },
    mounted() {
      if (this.isEditMode) {
        this.getDetail(this.id)
      }
    },
    beforeRouteLeave(to, from, next) {
      if (this.isSaving) {
        return next(false)
      }
      if (!this.isEdited) {
        return next()
      }
  
      this.$confirm(this.$t('message.leaveWithUnsavedChanges'), this.$t('common.warning'), {
        confirmButtonText: this.$t('button.leave'),
        cancelButtonText: this.$t('button.cancel'),
        type: 'warning',
      }).then(() => next()).catch(() => next(false))
  
      return next(false)
    },
    methods: {
      goToListPage() {
        this.$router.push({ path: '/trending/banner-schedules' })
      },
      handleAutoFill() {
        let bannerIds = this.bannerIds.split('\n')

        if (bannerIds.length === 4) {
          this.form.date = bannerIds[0]
          this.form.home_carousel_banner_ids = `${bannerIds[1]},${bannerIds[2]}`
          this.form.home_right_banner_ids = bannerIds[3]
          this.form.app_promotion_banner_id = bannerIds[1]
        }
      },
      handleCancel() {
        this.goToListPage()
      },
      handleSave() {
        this.$refs.form.validate().then(() => {
          if (this.isEditMode) {
            this.editBannerSchedules(this.form, this.id)
          } else {
            this.addBannerSchedules(this.form)
          }
        }).catch((error) => {
          console.error(error);
          this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), this.$t('common.oops'), {
            confirmButtonText: this.$t('button.ok'),
          });
        })
      },
      handleSaveSuccess() {
        this.isSaving = false
        this.isEdited = false
        this.goToListPage()
      },
      handleSaveFail(error) {
        this.isSaving = false
        this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
          type: 'error',
          confirmButtonText: this.$t('button.ok'),
        })
      },
      async getDetail(id) {
        this.isLoading = true
        try {
          const { data } = await getBannerScheduleDetail(id)
          this.form = data
          this.$nextTick(() => {
            this.isEdited = false
          })
        } catch (error) {
          console.error(error);
          this.$alert(this.$t('error.brandNotFound'), this.$t('common.error'), {
            type: 'error',
            confirmButtonText: this.$t('button.ok'),
          }).then(() => {
            this.goToListPage()
          })
        } finally {
          this.isLoading = false
        }
      },
      async editBannerSchedules(form) {
        this.isSaving = true
        try {
          await editBannerSchedule(form, this.id)
          this.handleSaveSuccess()
        } catch (error) {
          this.handleSaveFail(error)
        }
      },
      async addBannerSchedules(form) {
        this.isSaving = true
        try {
          await addBannerSchedule(form)
          this.handleSaveSuccess()
        } catch (error) {
          this.handleSaveFail(error)
        }
      },
    },
  }
  </script>
  
  <style lang="scss" scoped>
  @import "~@/assets/styles/view-base.scss";
  @import "~@/assets/styles/table.scss";
  @import "~@/assets/styles/form.scss";
  
  .el-form-item {
    padding-bottom: 6px;
  }
  
  .table-img {
    width: unset;
  }
  
  ::v-deep {
    .el-form-item__label {
      + .el-form-item__content {
        > *:not(.el-input):not(.el-textarea):not(.multiselect) {
          margin-top: 4px;
        }
      }
    }
    .el-table .el-table__row td {
      border: none;
      vertical-align: top;
    }
    .el-select__input.is-mini {
      height: auto;
    }
  }
  </style>
  